import { createSelector } from "reselect"
import { get, chain, map, filter, first, isNil, merge, mapKeys, reduce, update, mergeWith, omit } from "lodash"
import { getTickerAndMicFromContributor } from "helpers/securities"
import { getAllAvailableRollups } from "helpers/rollups"
import { computeSelectedGroupSiblings, computeSelectedGroupValue, computeSortedContributors, computeSortedGroupContributors, positionSetContributorsSummaryText } from "helpers/contributors"
import { getViewType, getPortfolioName, getShowNotHeld } from "modules/portfolio-root/selectors/portfolios"
import { getTagCategories, getAttributeCategories } from "modules/portfolio-root/selectors"
import { getDisplayTypeName } from "modules/analyze-composition/selectors/items"
import { computeGroupedContributorsCSV, computeContributorsGroupCSV, computeContributorsCSV } from "helpers/csv"
import { computeSelectedGroup } from "helpers/analyze"
import { VIEW_TYPES } from "helpers/base"

const EMPTY_OBJECT = {}

function computeNotHeldBucket(contributors) {
  return chain(contributors)
    .filter(item => !!item.notHeld)
    .reduce((acc, value) => {
      acc.size += value.percentEquity

      // Beta can be null for some securities
      const predicted = get(value, "predicted")
      if (!isNil(predicted)) {
        acc.factors.predicted = predicted + (acc.factors.predicted || 0)
      }
      const historical = get(value, "historical")
      if (!isNil(historical)) {
        acc.factors.historical = historical + (acc.factors.historical || 0)
      }

      return acc
    }, {
      id: "NOT HELD",
      notHeld: true,
      size: 0,
      factors: {},
    })
    .value()
}

function mapBetaContributors(data, showNotHeld) {
  const result = chain(data)
    .filter(item => showNotHeld || !item.notHeld)
    .map(security => {
      const { ticker, mic: exchange } = getTickerAndMicFromContributor(security)

      return {
        id: security.id,
        country: security.country,
        description: security.description,
        classification: get(security, "classification.name"),
        currency: security.currency,
        assetClass: security.assetClass,
        assetSubclass: security.assetSubclass,
        size: security.percentEquity,
        notHeld: security.notHeld,
        bloombergTicker: security.bloombergTicker,
        modelProviderId: security.modelProviderId,
        factors: {
          predicted: security.predicted,
          historical: security.historical,
          custom: security.dynamicPredicted,
        },
        ticker,
        exchange,
      }
    })
    .value()

  if (!showNotHeld) {
    result.push(computeNotHeldBucket(data))
  }
  return result
}

const getContributorsData = state => get(state, "global.data.betaContributors.data.model.simulation.betaContributors.0.contributors")
const getGroupedContributorsData = state => get(state, "global.data.groupedBetaContributors.data.model.simulation.groupedBetaContributors.0.groupedContributors")
const getDynamicBetaContributorsData = state => get(state, "global.data.dynamicBetaContributors.data.model.simulation.betaContributors.0.contributors")
const getDynamicBetaGroupedContributorsData = state => get(state, "global.data.dynamicGroupedBetaContributors.data.model.simulation.groupedBetaContributors.0.groupedContributors")
const getSelected = state => get(state, "modules.analyzeBetaDetails.selected", EMPTY_OBJECT)
const getRawSelectedGroup = (state, props) => get(props, "selectedGroup", EMPTY_OBJECT)
const getContributorsGrouping = (state, props) => get(props, "contributorsGrouping")
const getOrder = state => get(state, "modules.analyzeBetaDetails.order", EMPTY_OBJECT)
const getClassifications = (state) => get(state, "global.data.classifications.data.classifications")
const getSelectedDate = (state, props) => props.selectedDate

export const getGroupBy = (state, props) => get(props, "contributorsGrouping.id")

export const getCombinedContributorsData = createSelector(
  [getContributorsData, getDynamicBetaContributorsData],
  // merge() mutates its first arg, whose values are live Redux state objects; merge both keyed
  // maps into a fresh {} instead so the store isn't mutated (RTK immutableCheck).
  (betaContributors, dynamicBetaContributors) => merge(
    {},
    mapKeys(betaContributors, (contributor) => contributor.id),
    mapKeys(dynamicBetaContributors, (contributor) => contributor.id),
  ),
)

function groupReducer(groups, group) {
  return update(groups, group.id, existing => merge(existing || {}, { ...group, contributors: mapKeys(group.contributors, contributor => contributor.id) }))
}

// To combine the grouped contributors data from the beta and dynamic beta endpoints
// We need to first reduce the two data sets into a single object with the group id as the key
// Then we merge the two objects together, using the a customizer function that merges the nested contributors (they would be overwritten otherwise)
// eg. { 'groupA': { id: 'groupA', contributors: { 'a': { id, historical, predicted, dynamicPredicted }, ... } }, ... }
export const getCombinedGroupedContributorsData = createSelector(
  [getGroupedContributorsData, getDynamicBetaGroupedContributorsData],
  (betaGroupedContributors, dynamicBetaGroupedContributors) => mergeWith(
    reduce(betaGroupedContributors, groupReducer, {}),
    reduce(dynamicBetaGroupedContributors, groupReducer, {}),
    (betaGroup, dynamicBetaGroup) => ({
      ...omit(betaGroup, "contributors"),
      ...omit(dynamicBetaGroup, "contributors"),
      contributors: merge(betaGroup.contributors, dynamicBetaGroup.contributors),
    }),
  ),
)

export const getSelectedGroup = createSelector(
  [getRawSelectedGroup, getGroupedContributorsData],
  computeSelectedGroup,
)

export const getIncludeNotHeld = createSelector(
  [getViewType],
  (viewType) => viewType === VIEW_TYPES.ACTIVE,
)

const getSelectedField = createSelector(
  [getSelected],
  (selected) => get(selected, "field", "total"),
)

const getContributors = createSelector(
  [getCombinedContributorsData, getShowNotHeld],
  mapBetaContributors,
)

const getFilteredContributors = createSelector(
  [getContributors, getSelectedField],
  (data, selectedField) =>
    filter(data, security => {
      return get(security, selectedField)
    }),
)

export const getSortedContributors = createSelector(
  [getFilteredContributors, getSelectedField, getOrder],
  computeSortedContributors,
)

const getGroupedContributors = createSelector(
  [getCombinedGroupedContributorsData, getShowNotHeld],
  (data, showNotHeld) => map(data, group => ({
    ...group,
    factors: {
      historical: group.historical,
      predicted: group.predicted,
      custom: group.dynamicPredicted,
    },
    contributors: mapBetaContributors(group.contributors, showNotHeld),
  })),
)

const getFilteredGroupedContributors = createSelector(
  [getGroupedContributors, getSelectedField, getSelectedGroup],
  (data, selectedField, selectedGroup) => {
    return chain(data)
      .filter(group => !selectedGroup || selectedGroup.id === group.id)
      .map(group => ({
        ...group,
        contributors: filter(group.contributors, security => get(security, selectedField)),
      }))
      .value()
  },
)

export const getSortedGroupContributors = createSelector(
  [getFilteredGroupedContributors, getSelectedField, getOrder],
  computeSortedGroupContributors,
)

const getAccountRollups = createSelector(
  [getTagCategories, getClassifications, getAttributeCategories],
  (tagCategories, classifications, attributeCategories) => ({
    tagCategories,
    classifications,
    attributeCategories,
  }),
)
export const getAvailableRollups = createSelector(
  [getAccountRollups, getSelectedDate, getViewType],
  getAllAvailableRollups,
)

export const getContributorsSummaryText = createSelector(
  [getContributorsGrouping, getSortedContributors, getSortedGroupContributors, getSelectedGroup, getShowNotHeld, getViewType],
  positionSetContributorsSummaryText,
)

export const getCsvData = createSelector(
  [getSortedContributors, getSortedGroupContributors, getSelected, getContributorsGrouping, getPortfolioName, getSelectedDate, getViewType, getSelectedGroup, getDisplayTypeName],
  (data, groupedData, selected, contributorsGrouping, portfolioName, selectedDate, viewType, selectedGroup, displayType) => {
    if (selectedGroup) {
      const group = first(groupedData)
      return computeGroupedContributorsCSV({
        type: displayType,
        portfolioName,
        selected,
        selectedDate,
        viewType,
        group,
        net: true,
      })
    }

    if (contributorsGrouping) {
      return computeContributorsGroupCSV({
        type: displayType,
        portfolioName,
        selected,
        selectedDate,
        viewType,
        contributorsGrouping,
        data: groupedData,
        net: true,
      })
    }

    return computeContributorsCSV({
      type: displayType,
      portfolioName,
      selected,
      selectedDate,
      viewType,
      data,
      net: true,
    })
  },
)

export const getSelectedGroupValue = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  computeSelectedGroupValue,
)

export const getSelectedGroupSiblings = createSelector(
  [getSelectedGroup, getGroupedContributors, getSelectedField],
  computeSelectedGroupSiblings,
)
